import { useEffect, useState } from 'react';

import { ciede2000 } from './color-diff';
import { suggestions } from './constants';
import { calculateColorDeltas, colorHexToNumber } from './helpers';

// srgb == standard RGB

// the depth tells us how many pixels in from the edge we need to go
const isEdgePixel = (pixelIndex: number, width: number, height: number, depth: number) => {
  const totalPixels = width * height;
  const mod = pixelIndex % width;
  if (pixelIndex < width * depth) {
    return true; // first row(s)
  } else if (pixelIndex > totalPixels - width * depth) {
    return true; // last row(s)
  } else if (mod < depth || mod > width - depth) {
    return true;
  } else {
    return false;
  }
};

const colorsEqual = (
  c1: number | undefined,
  c2: number | undefined,
  threshold = suggestions.colorEqualThreshold,
): boolean => {
  if (c1 === undefined || c2 === undefined) {
    return false;
  }
  return ciede2000(c1, c2) < threshold;
};
const containsColor = (colors: number[], colorToCheck: number | undefined): boolean => {
  if (colorToCheck === undefined) {
    return false;
  }
  const contains = colors.find((color) => colorsEqual(color, colorToCheck)) !== undefined;
  return contains;
};

/** Deduplicates colors using the ciede2000 algorithm */
const deduplicate = (colors: number[]): number[] => {
  const result: number[] = [];
  for (const col of new Set(colors)) {
    if (!containsColor(result, col)) {
      result.push(col);
    }
  }
  return result;
};

/** Uses the CIEDE2000 algorithm to find groups of colors, and return the center color of the groups. */
function getColorCenters(
  uniqueColors: number[],
  colorCounts: ColorCounts,
  totalSignificanceThreshold: number,
): number[] {
  let remainingColors = [...uniqueColors];
  const colorCenters: number[] = [];
  // remove colors from remaining colours until they're all gone
  while (remainingColors.length > 0) {
    const cluster = calculateColorDeltas(remainingColors[0], remainingColors, 5);
    const pixelCount = [...new Set(cluster.values)].reduce((curr, next) => curr + colorCounts[next], 0);

    remainingColors = remainingColors.filter((color) => !cluster.values.includes(color));
    if (pixelCount > totalSignificanceThreshold) {
      colorCenters.push(cluster.center);
    }
  }
  return colorCenters;
}

interface ColorCounts {
  [color: string]: number;
}
const white = 0xffffff;
const black = 0x0;

/** Adds default colors and prevents camouflage */
const withDefaultColors = (colors: number[], allImageColors: number[]): number[] => {
  const result = [...colors];
  const camouflageColor = allImageColors.length === 1 ? allImageColors[0] : undefined;

  // check if the image contains either of the colours we will add.
  const imageContainsWhite = containsColor(allImageColors, white);
  const imageContainsBlack = containsColor(allImageColors, black);
  // used later to decide what order to add the colors
  let needsBlack = false;
  let needsWhite = false;

  if (
    !containsColor(result, white) &&
    !colorsEqual(white, camouflageColor, suggestions.camouflageThreshold)
  ) {
    needsWhite = true;
  }
  if (
    !containsColor(result, black) &&
    !colorsEqual(black, camouflageColor, suggestions.camouflageThreshold) &&
    result.length < 3
  ) {
    needsBlack = true;
  }

  // decide what order to add the default colors, based on the image
  switch (true) {
    // if the image contins white but not black, then use black as the first color
    case needsBlack && needsWhite && !imageContainsBlack && imageContainsWhite: {
      result.push(black);
      result.push(white);
      break;
    }
    // otherwise, add white first
    default: {
      if (needsWhite) result.push(white);
      if (needsBlack) result.push(black);
    }
  }

  return result;
};
/** Runs an algorithm to get the 'top' colors from the canvas
 * Looks at only the "edge pixels" and counts them by value
 * Returns the colors with the most counts
 */
const getTopColors = (canvas: HTMLCanvasElement): number[] | undefined => {
  const context = canvas.getContext('2d');
  const imgData = context?.getImageData(0, 0, canvas.width, canvas.height); // return RGBA

  const length = imgData?.data.length;
  if (length) {
    const totalPixels = length / 4;
    const allPixelColors: number[] = [];
    const edgePixels: number[] = [];
    const colorCounts: ColorCounts = {};
    const colorEdgeCounts: ColorCounts = {};

    let nEdgePixels = 0;
    for (let pixelIndex = 0; pixelIndex < totalPixels; pixelIndex++) {
      const r = imgData.data[pixelIndex * 4];
      const g = imgData.data[pixelIndex * 4 + 1];
      const b = imgData.data[pixelIndex * 4 + 2];
      const alpha = imgData.data[pixelIndex * 4 + 3];

      const x =
        r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
      const finalVal = colorHexToNumber(x);
      if (!isNaN(finalVal)) {
        if (alpha) {
          colorCounts[finalVal] = (colorCounts[finalVal] || 0) + 1;
          allPixelColors.push(finalVal); // add the value to the array
        }
        if (isEdgePixel(pixelIndex, canvas.width, canvas.height, suggestions.pixelEdgeDepth)) {
          nEdgePixels++; // increment edge pixels even if transparent
          if (alpha) {
            // only get colours that arent transparent
            colorEdgeCounts[finalVal] = (colorEdgeCounts[finalVal] || 0) + 1;
            edgePixels.push(finalVal);
          }
        }
      }
    }

    // only consider colors with more than 5 pixels
    const significantColors = allPixelColors.filter((color) => colorCounts[color] > 5);
    // calculate edge significance threshold
    const edgeSignificanceThreshold = nEdgePixels * suggestions.edgeSignificance;

    const uniqueColors = [...new Set(significantColors)];
    const uniqueEdgeColors = [...new Set(edgePixels)];
    // foreach unique, check distance to other uniques
    const edgeColorCenters = getColorCenters(uniqueEdgeColors, colorEdgeCounts, edgeSignificanceThreshold);

    if (edgeColorCenters.length < 3) {
      return withDefaultColors(deduplicate(edgeColorCenters), uniqueColors);
    } else {
      return withDefaultColors(edgeColorCenters.slice(0, 2), uniqueColors);
    }
  }
};

interface ColorSuggestions {
  colors?: number[];
  loading?: boolean;
}
/** Generate bg color suggestions. Null will prevent it running */
export default function useColorSuggestions(imgSrc: string | null): ColorSuggestions {
  const [suggestions, setSuggestions] = useState<ColorSuggestions>({ loading: true });

  useEffect(() => {
    if (imgSrc) {
      const image = new Image();
      image.src = imgSrc;
      image.crossOrigin = ''; // allow anonymous
      let canvas: HTMLCanvasElement;
      image.onload = () => {
        const aspectRatio = Math.max(image.width / image.height, 1);
        canvas = document.createElement('canvas');
        canvas.width = 180;
        canvas.height = Math.round(320 / aspectRatio);
        const context = canvas.getContext('2d');
        context?.drawImage(image, 0, 0, canvas.width || 10, canvas.height || 10);
        if (canvas) {
          const top = getTopColors(canvas);
          if (top) {
            setSuggestions({ colors: [...new Set(top)] });
          }
        }
      };

      return () => canvas?.remove();
    }
  }, [imgSrc]);

  return suggestions;
}
